import { loop, Loop } from 'redux-loop';

import auth0Client from 'src/Auth';
import { API_BASE } from 'src/config';
import {
  JobId,
  SingleCalculationResultsArbitraryCooling,
  SteelValuesArbitraryCooling
} from '../../types/Calculation';
import { HttpError } from '../utils/HttpError';
import { runLoopFetch } from '../utils/loop';

export const ARBITRARY_COOLING_FETCH_START =
  'ARBITRARY_COOLING_FETCH_START';
export const ARBITRARY_COOLING_FETCH_SUCCESS =
  'ARBITRARY_COOLING_FETCH_SUCCESS';
export const ARBITRARY_COOLING_FETCH_FAILURE =
  'ARBITRARY_COOLING_FETCH_FAILURE';
export const ARBITRARY_COOLING_POLL_SUCCESS =
  'ARBITRARY_COOLING_POLL_SUCCESS';
export const ARBITRARY_COOLING_POLL_FAILURE =
  'ARBITRARY_COOLING_POLL_FAILURE';

type CalculationAction =
  | {
      type: typeof ARBITRARY_COOLING_FETCH_START;
      steelValues: SteelValuesArbitraryCooling;
    }
  | { type: typeof ARBITRARY_COOLING_FETCH_SUCCESS; payload: JobId }
  | {
      type: typeof ARBITRARY_COOLING_POLL_SUCCESS;
      payload: SingleCalculationResultsArbitraryCooling;
    }
  | { type: typeof ARBITRARY_COOLING_FETCH_FAILURE; payload?: any }
  | { type: typeof ARBITRARY_COOLING_POLL_FAILURE; payload?: any };

export interface CalculationState {
  singleCalcResults: SingleCalculationResultsArbitraryCooling | null;
  fetching: boolean;
  error: { status?: number; message: string } | null;
}

const initialState: CalculationState = {
  fetching: false,
  singleCalcResults: null,
  error: null
};

const resultKeys: Array<keyof SingleCalculationResultsArbitraryCooling> = [
  'time_s',
  'temperature_C',
  'austenite_fraction',
  'ferrite_fraction',
  'pearlite_fraction',
  'bainite_fraction',
  'martensite_fraction',
  'eutectoid_carbon_selected'
];

const parseBackendJsonString = (value: string): any => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return JSON.parse(
      value.replace(/\bNaN\b|[-+]?Infinity\b/g, 'null')
    );
  }
};

const orderedObjectValues = (value: { [index: string]: any }): any[] =>
  Object.keys(value)
    .sort((left, right) => {
      const leftIndex = Number(left);
      const rightIndex = Number(right);
      return Number.isFinite(leftIndex) && Number.isFinite(rightIndex)
        ? leftIndex - rightIndex
        : left.localeCompare(right);
    })
    .map(index => value[index]);

const recordsToColumns = (records: any[]): any =>
  resultKeys.reduce(
    (columns, key) => ({
      ...columns,
      [key]: records.map(record => record && record[key])
    }),
    {}
  );

const unwrapResult = (payload: any): any => {
  let result = payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (typeof result === 'string') {
      result = parseBackendJsonString(result);
      continue;
    }

    if (result && result.Result !== undefined) {
      result = result.Result;
      continue;
    }

    if (result && result.result !== undefined) {
      result = result.result;
      continue;
    }

    break;
  }

  return result;
};

export const normalizeArbitraryCoolingResult = (
  payload: any
): SingleCalculationResultsArbitraryCooling => {
  let result = unwrapResult(payload);

  if (result && typeof result.error === 'string') {
    throw new Error(result.error);
  }

  if (Array.isArray(result)) {
    result = recordsToColumns(result);
  } else if (
    result &&
    Array.isArray(result.columns) &&
    Array.isArray(result.data)
  ) {
    const columnIndexes = result.columns.reduce(
      (indexes: { [name: string]: number }, name: string, index: number) => ({
        ...indexes,
        [name]: index
      }),
      {}
    );
    result = resultKeys.reduce(
      (columns, key) => ({
        ...columns,
        [key]: result.data.map((row: any[]) => row[columnIndexes[key]])
      }),
      {}
    );
  } else if (result && !result.time_s && typeof result === 'object') {
    const possibleRecords = orderedObjectValues(result);
    if (
      possibleRecords.length > 0 &&
      possibleRecords[0] &&
      possibleRecords[0].time_s !== undefined
    ) {
      result = recordsToColumns(possibleRecords);
    }
  }

  const normalized = {} as SingleCalculationResultsArbitraryCooling;
  resultKeys.forEach(key => {
    const column = result && result[key];
    const values = Array.isArray(column)
      ? column
      : column && typeof column === 'object'
        ? orderedObjectValues(column)
        : null;

    if (!values) {
      throw new Error(
        'Unexpected arbitrary-cooling result: ' + key + ' is missing.'
      );
    }

    normalized[key] = values.map((value: any) => Number(value));
  });

  const expectedLength = normalized.time_s.length;
  if (expectedLength === 0) {
    throw new Error(
      'Unexpected arbitrary-cooling result: result arrays are empty.'
    );
  }

  resultKeys.forEach(key => {
    if (normalized[key].length !== expectedLength) {
      throw new Error(
        'Unexpected arbitrary-cooling result: arrays have different lengths.'
      );
    }
    if (!normalized[key].every(Number.isFinite)) {
      throw new Error(
        'Unexpected arbitrary-cooling result: ' +
          key +
          ' contains invalid values.'
      );
    }
  });

  return normalized;
};

export const fetchArbitraryCoolingCalculation = async (
  steelValues: SteelValuesArbitraryCooling
): Promise<JobId> => {
  const res = await fetch(API_BASE + '/calculation/arbitraryCooling', {
    body: JSON.stringify(steelValues),
    credentials: 'same-origin',
    headers: {
      Authorization: 'Bearer ' + auth0Client.getIdToken(),
      'content-type': 'application/json'
    },
    method: 'POST'
  });

  if (!res.ok) {
    throw new HttpError({ status: res.status, message: res.statusText });
  }
  return res.json();
};

export const initFetchArbitraryCooling = (
  steelValues: SteelValuesArbitraryCooling
) => ({
  steelValues,
  type: ARBITRARY_COOLING_FETCH_START
});

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchArbitraryCoolingResult = async (
  jobid: JobId,
  count: number = 0,
  waitFor: (ms: number) => Promise<any> = wait
): Promise<SingleCalculationResultsArbitraryCooling> => {
  const id = String((jobid as any).id || (jobid as any).jobid || jobid);
  const url =
    API_BASE +
    '/calculation/arbitraryCoolingResult/' +
    encodeURIComponent(id) +
    '?wait=true&maxWaitMs=25000';
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Authorization: 'Bearer ' + auth0Client.getIdToken(),
      'content-type': 'application/json'
    },
    method: 'GET'
  });

  if (!res.ok) {
    throw new HttpError({ status: res.status, message: res.statusText });
  }
  if (res.status === 202) {
    if (count >= 80) {
      throw new Error('Maximum arbitrary-cooling polling retries reached.');
    }
    await waitFor(15000);
    return fetchArbitraryCoolingResult(jobid, count + 1, waitFor);
  }
  if (res.status !== 200) {
    throw new HttpError({
      status: res.status,
      message: 'Unexpected arbitrary-cooling result status.'
    });
  }

  return normalizeArbitraryCoolingResult(await res.json());
};

const toError = (payload: any) => ({
  status: payload && payload.status,
  message: (payload && payload.message) || 'Arbitrary cooling request failed'
});

export function calculation(
  state: CalculationState = initialState,
  action: CalculationAction
): CalculationState | Loop<CalculationState, any> {
  switch (action.type) {
    case ARBITRARY_COOLING_FETCH_START:
      return loop(
        { ...state, fetching: true, singleCalcResults: null, error: null },
        runLoopFetch(
          fetchArbitraryCoolingCalculation,
          ARBITRARY_COOLING_FETCH_SUCCESS,
          ARBITRARY_COOLING_FETCH_FAILURE,
          [action.steelValues]
        )
      );

    case ARBITRARY_COOLING_FETCH_SUCCESS:
      return loop(
        { ...state, fetching: true, error: null },
        runLoopFetch(
          fetchArbitraryCoolingResult,
          ARBITRARY_COOLING_POLL_SUCCESS,
          ARBITRARY_COOLING_POLL_FAILURE,
          [action.payload, 0]
        )
      );

    case ARBITRARY_COOLING_POLL_SUCCESS:
      return {
        ...state,
        fetching: false,
        singleCalcResults: action.payload,
        error: null
      };

    case ARBITRARY_COOLING_FETCH_FAILURE:
    case ARBITRARY_COOLING_POLL_FAILURE:
      return {
        ...state,
        fetching: false,
        singleCalcResults: null,
        error: toError(action.payload)
      };

    default:
      return state;
  }
}
